import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Minus, Plus } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import api from "../lib/api";
import { applyDocumentMetadata } from "../lib/metadata";
import productPlaceholder from "../assets/product-placeholder.svg";

const productImageFallback = productPlaceholder;

const DEFAULT_BRANDING = {
  appName: "PingUp Shop",
  logoUrl: "",
  primaryColor: "#38bdf8",
  accentColor: "#f472b6",
};

function isHexColor(value) {
  return /^#[0-9a-fA-F]{6}$/.test(String(value || "").trim());
}

function normalizeHexColor(value, fallback) {
  const normalized = String(value || "").trim();
  return isHexColor(normalized) ? normalized.toLowerCase() : fallback;
}

function hexToRgba(hexColor, alpha) {
  const hex = normalizeHexColor(hexColor, "#38bdf8").slice(1);
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const normalizedAlpha = Math.max(0, Math.min(1, Number(alpha) || 0));
  return `rgba(${red}, ${green}, ${blue}, ${normalizedAlpha})`;
}

function roundCurrency(value) {
  const parsed = Number(value) || 0;
  return Math.round((parsed + Number.EPSILON) * 100) / 100;
}

function formatCurrency(value) {
  const amount = Number(value) || 0;
  return `${amount.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function resolveVariantPrice(variant) {
  const effectiveDiscounted = Number(variant?.effectiveDiscounted);
  if (Number.isFinite(effectiveDiscounted) && effectiveDiscounted > 0) {
    return roundCurrency(effectiveDiscounted);
  }

  const effectiveBase = Number(variant?.effectiveBase);
  if (Number.isFinite(effectiveBase) && effectiveBase > 0) {
    return roundCurrency(effectiveBase);
  }

  const rawPrice = Number(variant?.price) || 0;
  const discountPercent = Number(variant?.discount) || 0;
  if (discountPercent <= 0) return roundCurrency(rawPrice);
  return roundCurrency(rawPrice - (rawPrice * discountPercent) / 100);
}

function resolveBasePrice(product) {
  if (!product) return 0;

  if (product.hasVariants && Array.isArray(product.variants) && product.variants.length) {
    const values = product.variants
      .map((variant) => resolveVariantPrice(variant))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (values.length) {
      return Math.min(...values);
    }
  }

  const effectiveDiscounted = Number(product.price?.effectiveDiscounted);
  const effectiveBase = Number(product.price?.effectiveBase);
  if (Number.isFinite(effectiveDiscounted) && effectiveDiscounted > 0) return roundCurrency(effectiveDiscounted);
  if (Number.isFinite(effectiveBase) && effectiveBase > 0) return roundCurrency(effectiveBase);
  return roundCurrency(Number(product.price?.base) || 0);
}

function resolveOptionKey(option, index) {
  return String(option?._id || `option-${index}`);
}

function resolveChoiceKey(choice, index) {
  return String(choice?._id || `choice-${index}`);
}

function resolveChoiceAddon(choice) {
  const value = Number(choice?.priceAddon);
  return Number.isFinite(value) ? value : 0;
}

function formatAddonLabel(value) {
  const parsed = Number(value) || 0;
  if (parsed === 0) {
    return "Incluso";
  }

  const symbol = parsed > 0 ? "+" : "-";
  return `${symbol} ${formatCurrency(Math.abs(parsed))}`;
}

function normalizeProductOptions(options) {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option, index) => {
      const type = String(option?.type || "text");
      const label = String(option?.label || "").trim();
      if (!label) {
        return null;
      }

      const choices = Array.isArray(option?.choices)
        ? option.choices
            .map((choice, choiceIndex) => {
              const choiceLabel = String(choice?.label || "").trim();
              if (!choiceLabel) return null;

              return {
                _key: resolveChoiceKey(choice, choiceIndex),
                label: choiceLabel,
                description: String(choice?.description || "").trim(),
                priceAddon: resolveChoiceAddon(choice),
              };
            })
            .filter(Boolean)
        : [];

      return {
        _key: resolveOptionKey(option, index),
        type,
        label,
        description: String(option?.description || "").trim(),
        required: Boolean(option?.required),
        choices,
      };
    })
    .filter(Boolean);
}

function normalizeExtraInfoBlocks(extraInfo) {
  if (!Array.isArray(extraInfo)) {
    return [];
  }

  return extraInfo
    .map((block, index) => {
      const type = String(block?.type || "text");

      if (type === "table") {
        const headers = Array.isArray(block?.headers)
          ? block.headers.map((header) => String(header || "").trim()).filter(Boolean)
          : [];

        const rows = Array.isArray(block?.rows)
          ? block.rows
              .map((row) => (Array.isArray(row) ? row.map((cell) => String(cell || "").trim()) : []))
              .filter((row) => row.some((cell) => cell))
          : [];

        if (!headers.length || !rows.length) {
          return null;
        }

        return {
          _key: String(block?._id || `extra-${index}`),
          type: "table",
          title: String(block?.title || "").trim(),
          headers,
          rows,
        };
      }

      if (type === "faq") {
        const items = Array.isArray(block?.items)
          ? block.items
              .map((item, itemIndex) => {
                const question = String(item?.question || "").trim();
                const answer = String(item?.answer || "").trim();
                if (!question || !answer) return null;

                return {
                  _key: String(item?._id || `faq-${index}-${itemIndex}`),
                  question,
                  answer,
                };
              })
              .filter(Boolean)
          : [];

        if (!items.length) {
          return null;
        }

        return {
          _key: String(block?._id || `extra-${index}`),
          type: "faq",
          title: String(block?.title || "").trim(),
          items,
        };
      }

      const content = String(block?.content || "").trim();
      if (!content) {
        return null;
      }

      return {
        _key: String(block?._id || `extra-${index}`),
        type: "text",
        content,
      };
    })
    .filter(Boolean);
}

function buildOptionValueMap(options) {
  if (!Array.isArray(options)) {
    return {};
  }

  return options.reduce((accumulator, option, index) => {
    const optionKey = resolveOptionKey(option, index);
    const type = String(option?.type || "text");

    if (type === "multiple_choice") {
      accumulator[optionKey] = [];
      return accumulator;
    }

    accumulator[optionKey] = "";
    return accumulator;
  }, {});
}

function isOptionValueMissing(option, value) {
  if (!option?.required) return false;

  if (option.type === "multiple_choice") {
    return !Array.isArray(value) || value.length === 0;
  }

  return String(value || "").trim() === "";
}

export default function ProductDetailPage() {
  const navigate = useAppNavigate();
  const { tenantId = "default", productId = "" } = useParams();

  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [product, setProduct] = useState(null);
  const [inventoryManagement, setInventoryManagement] = useState(false);
  const [customerReady, setCustomerReady] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [optionValues, setOptionValues] = useState({});

  const primaryColor = useMemo(
    () => normalizeHexColor(branding.primaryColor, DEFAULT_BRANDING.primaryColor),
    [branding.primaryColor]
  );
  const accentColor = useMemo(
    () => normalizeHexColor(branding.accentColor, DEFAULT_BRANDING.accentColor),
    [branding.accentColor]
  );

  const tenantTheme = useMemo(
    () => ({
      "--tenant-primary": primaryColor,
      "--tenant-accent": accentColor,
    }),
    [accentColor, primaryColor]
  );

  const selectedRowStyle = useMemo(
    () => ({
      borderColor: primaryColor,
      backgroundColor: hexToRgba(primaryColor, 0.08),
    }),
    [primaryColor]
  );

  const selectedDotStyle = useMemo(
    () => ({
      borderColor: primaryColor,
      backgroundColor: primaryColor,
    }),
    [primaryColor]
  );

  useEffect(() => {
    sessionStorage.setItem("webapp_tenant_id", tenantId);
  }, [tenantId]);

  useEffect(() => {
    let active = true;
    setCustomerReady(false);

    api.get("/webapp/customers/me", {
      headers: { "x-tenant-id": tenantId },
    })
      .catch(() => {
        // Guests may still view products; this request only resolves pricing identity.
      })
      .finally(() => {
        if (active) setCustomerReady(true);
      });

    return () => { active = false; };
  }, [tenantId]);

  useEffect(() => {
    if (!customerReady) return undefined;
    let active = true;

    async function loadData() {
      setLoading(true);
      setError("");

      try {
        const [{ data: configResponse }, { data: productResponse }] = await Promise.all([
          api.get("/webapp/config", {
            headers: { "x-tenant-id": tenantId },
          }),
          api.get(`/webapp/products/${productId}`, {
            headers: { "x-tenant-id": tenantId },
          }),
        ]);

        if (!active) return;

        const configPayload = configResponse?.data || {};
        const nextBranding = {
          appName: configPayload?.branding?.appName || DEFAULT_BRANDING.appName,
          logoUrl: configPayload?.branding?.logoUrl || "",
          primaryColor: normalizeHexColor(configPayload?.branding?.primaryColor, DEFAULT_BRANDING.primaryColor),
          accentColor: normalizeHexColor(configPayload?.branding?.accentColor, DEFAULT_BRANDING.accentColor),
        };

        const nextProduct = productResponse?.data || null;

        setBranding(nextBranding);
        setProduct(nextProduct);
        setInventoryManagement(configPayload?.inventoryManagement === true);
        setQuantity(1);
        setNotes("");
        setOptionValues(buildOptionValueMap(nextProduct?.options));

        if (nextProduct?.hasVariants && Array.isArray(nextProduct?.variants) && nextProduct.variants.length) {
          setSelectedVariantId(String(nextProduct.variants[0]._id));
        } else {
          setSelectedVariantId(null);
        }

        applyDocumentMetadata({
          title: nextProduct?.name ? `${nextProduct.name} | ${nextBranding.appName}` : nextBranding.appName,
          description: nextProduct?.description || "Dettaglio prodotto e configurazione ordine.",
          image: nextProduct?.images?.[0]?.url || nextBranding.logoUrl || "",
          siteName: nextBranding.appName,
          url: window.location.href,
        });
      } catch (requestError) {
        if (!active) return;
        setError(requestError?.response?.data?.error?.message || "Impossibile caricare il prodotto.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadData();

    return () => {
      active = false;
    };
  }, [customerReady, productId, tenantId]);

  const selectedVariant = useMemo(() => {
    if (!product?.hasVariants || !Array.isArray(product?.variants)) return null;
    return product.variants.find((variant) => String(variant._id) === String(selectedVariantId)) || null;
  }, [product, selectedVariantId]);

  const productOptions = useMemo(() => normalizeProductOptions(product?.options), [product?.options]);
  const extraInfoBlocks = useMemo(() => normalizeExtraInfoBlocks(product?.extraInfo), [product?.extraInfo]);

  const maxQuantity = inventoryManagement ? (Number(product?.availability?.maxQuantity) || null) : null;
  const soldOut = product?.availability?.status === "sold_out";

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    if (selectedVariant) return resolveVariantPrice(selectedVariant);
    return resolveBasePrice(product);
  }, [product, selectedVariant]);

  const optionsAddonTotal = useMemo(() => {
    if (!productOptions.length) return 0;

    return roundCurrency(
      productOptions.reduce((sum, option) => {
        const value = optionValues[option._key];

        if (option.type === "single_choice") {
          const selected = option.choices.find((choice) => choice._key === String(value || ""));
          return sum + (selected ? selected.priceAddon : 0);
        }

        if (option.type === "multiple_choice") {
          const selectedKeys = Array.isArray(value) ? value : [];
          const optionAddon = option.choices
            .filter((choice) => selectedKeys.includes(choice._key))
            .reduce((optionSum, choice) => optionSum + choice.priceAddon, 0);
          return sum + optionAddon;
        }

        return sum;
      }, 0)
    );
  }, [optionValues, productOptions]);

  const totalPrice = useMemo(
    () => roundCurrency(unitPrice * quantity + optionsAddonTotal),
    [optionsAddonTotal, quantity, unitPrice]
  );

  const missingRequiredCount = useMemo(() => {
    if (!productOptions.length) return 0;

    return productOptions.reduce((missing, option) => {
      const value = optionValues[option._key];
      return missing + (isOptionValueMissing(option, value) ? 1 : 0);
    }, 0);
  }, [optionValues, productOptions]);

  const canIncrement = !soldOut && (!maxQuantity || quantity < maxQuantity);
  const canDecrement = quantity > 1;
  const ctaDisabled = soldOut || missingRequiredCount > 0;

  const categoryLabel = String(product?.category?.name || "").toUpperCase() || "DETTAGLIO";
  const gallery = Array.isArray(product?.images)
    ? product.images.filter((image) => String(image?.url || "").trim())
    : [];
  const hasGallery = gallery.length > 0;

  const displayPrice = resolveBasePrice(product);
  const decorativeDiscount = Number(product?.price?.effectiveDiscounted);
  const hasDecorativeDiscount =
    !product?.hasVariants && displayPrice > 0 && Number.isFinite(decorativeDiscount) && decorativeDiscount < Number(product?.price?.effectiveBase || product?.price?.base);

  function updateOptionValue(optionKey, value) {
    setOptionValues((current) => ({
      ...current,
      [optionKey]: value,
    }));
  }

  function toggleMultipleChoice(optionKey, choiceKey) {
    setOptionValues((current) => {
      const previous = Array.isArray(current[optionKey]) ? current[optionKey] : [];
      const next = previous.includes(choiceKey)
        ? previous.filter((value) => value !== choiceKey)
        : [...previous, choiceKey];

      return {
        ...current,
        [optionKey]: next,
      };
    });
  }

  return (
    <div className="min-h-screen bg-[#efefef]" style={tenantTheme}>
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">
        <div className="sticky top-0 z-20 bg-white">
          <div className="flex items-center gap-3 px-5 pb-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/${tenantId}`)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#f1f5f9]"
              aria-label="Torna al catalogo"
            >
              <ArrowLeft className="h-4 w-4 text-[#334155]" />
            </button>
            <p className="min-w-0 flex-1 truncate text-right text-xs font-semibold tracking-[0.6px] text-[#64748b]">
              {categoryLabel}
            </p>
          </div>
        </div>

        {loading ? <div className="px-5 py-6 text-sm text-[#64748b]">Caricamento dettaglio prodotto...</div> : null}
        {error ? <div className="px-5 py-6 text-sm text-red-500">{error}</div> : null}

        {!loading && !error && product ? (
          <>
            <main className="pb-[190px]">
              {hasGallery ? (
                <section className="px-5 pb-2">
                  <div className="rounded-[20px] border border-[#e2e8f0] bg-white p-3">
                    <img
                      src={gallery[0].url}
                      alt={product?.name || "Prodotto"}
                      className="h-[250px] w-full rounded-[16px] object-cover"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = productImageFallback;
                      }}
                    />
                  </div>
                </section>
              ) : null}

              <section className="px-5 pb-8 pt-3">
                <h1 className="text-[20px] font-semibold leading-6 text-[#020617]">{product?.name || "Prodotto"}</h1>
                {product?.brandId?.name ? (
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-accent)]">
                    {product.brandId.name }
                  </p>
                ) : null}
                <p className="mt-2 text-sm leading-5 text-[#64748b]">{product?.description || ""}</p>
                {displayPrice > 0 ? (
                  <div className="mt-4 flex items-start justify-between gap-3 text-sm">
                    <span className="text-[#64748b]">
                      {product?.hasVariants ? "Prezzo al pezzo (a partire da):" : "Prezzo:"}
                    </span>
                    <div className="text-right">
                      {hasDecorativeDiscount ? (
                        <p className="text-xs text-[#94a3b8] line-through">
                          {formatCurrency(Number(product?.price?.effectiveBase || product?.price?.base) || 0)}
                        </p>
                      ) : null}
                      <p className="font-semibold text-[#020617]">{formatCurrency(displayPrice)}</p>
                    </div>
                  </div>
                ) : null}
              </section>

              {product?.hasVariants && Array.isArray(product?.variants) && product.variants.length ? (
                <section className="border-y border-[#e2e8f0] px-5 py-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-primary)]">
                    Varianti
                  </p>
                  <p className="mt-1 text-xs text-[#64748b]">Seleziona un formato prodotto.</p>
                  <div className="mt-5 space-y-3">
                    {product.variants.map((variant) => {
                      const variantId = String(variant._id);
                      const checked = variantId === String(selectedVariantId);
                      const variantPrice = resolveVariantPrice(variant);
                      const originalVariantPrice = Number(variant?.effectiveBase ?? variant?.price) || 0;
                      const hasVariantDiscount = variantPrice > 0 && variantPrice < originalVariantPrice;

                      return (
                        <button
                          key={variantId}
                          type="button"
                          onClick={() => setSelectedVariantId(variantId)}
                          className="flex w-full items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                          style={checked ? selectedRowStyle : undefined}
                        >
                          <span
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#94a3b8]"
                            style={checked ? { borderColor: primaryColor } : undefined}
                          >
                            {checked ? <span className="h-2 w-2 rounded-full" style={selectedDotStyle} /> : null}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#334155]">{variant?.name || "Variante"}</p>
                            {variant?.description ? (
                              <p className="truncate text-xs text-[#64748b]">{variant.description}</p>
                            ) : null}
                          </div>
                          <div className="text-right">
                            {hasVariantDiscount ? (
                              <p className="text-xs text-[#94a3b8] line-through">{formatCurrency(originalVariantPrice)}</p>
                            ) : null}
                            {variantPrice > 0 ? (
                              <p className="text-sm font-semibold text-[#020617]">{formatCurrency(variantPrice)}</p>
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              {productOptions.length ? (
                <section className="border-b border-[#e2e8f0] px-5 py-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-primary)]">
                    Configurazioni
                  </p>
                  <p className="mt-1 text-xs text-[#64748b]">Completa le opzioni richieste per questo prodotto.</p>

                  <div className="mt-4 space-y-4">
                    {productOptions.map((option) => {
                      const value = optionValues[option._key];
                      const missing = isOptionValueMissing(option, value);

                      return (
                        <div
                          key={option._key}
                          className="rounded-xl border border-[#e2e8f0] bg-white p-3"
                          style={missing ? { borderColor: hexToRgba(primaryColor, 0.45) } : undefined}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-[#0f172a]">
                                {option.label}
                                {option.required ? " *" : ""}
                              </p>
                              {option.description ? (
                                <p className="mt-0.5 text-xs text-[#64748b]">{option.description}</p>
                              ) : null}
                            </div>
                            {option.required ? (
                              <span className="rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                                Obbligatoria
                              </span>
                            ) : null}
                          </div>

                          {option.type === "text" ? (
                            <input
                              type="text"
                              value={String(value || "")}
                              onChange={(event) => updateOptionValue(option._key, event.target.value)}
                              placeholder="Scrivi qui..."
                              className="mt-3 h-10 w-full rounded-lg border border-[#e2e8f0] px-3 text-sm outline-none focus:border-[var(--tenant-primary)]"
                            />
                          ) : null}

                          {option.type === "number" ? (
                            <input
                              type="number"
                              value={String(value || "")}
                              onChange={(event) => updateOptionValue(option._key, event.target.value)}
                              placeholder="Inserisci un numero"
                              className="mt-3 h-10 w-full rounded-lg border border-[#e2e8f0] px-3 text-sm outline-none focus:border-[var(--tenant-primary)]"
                            />
                          ) : null}

                          {option.type === "date" ? (
                            <input
                              type="date"
                              value={String(value || "")}
                              onChange={(event) => updateOptionValue(option._key, event.target.value)}
                              className="mt-3 h-10 w-full rounded-lg border border-[#e2e8f0] px-3 text-sm outline-none focus:border-[var(--tenant-primary)]"
                            />
                          ) : null}

                          {option.type === "single_choice" ? (
                            <div className="mt-3 space-y-2">
                              {option.choices.map((choice) => {
                                const checked = String(value || "") === choice._key;

                                return (
                                  <button
                                    key={choice._key}
                                    type="button"
                                    onClick={() => updateOptionValue(option._key, choice._key)}
                                    className="flex w-full items-center gap-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                                    style={checked ? selectedRowStyle : undefined}
                                  >
                                    <span
                                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#94a3b8]"
                                      style={checked ? { borderColor: primaryColor } : undefined}
                                    >
                                      {checked ? <span className="h-2 w-2 rounded-full" style={selectedDotStyle} /> : null}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-[#0f172a]">{choice.label}</p>
                                      {choice.description ? (
                                        <p className="truncate text-xs text-[#64748b]">{choice.description}</p>
                                      ) : null}
                                    </div>
                                    <span className="text-xs font-medium text-[#334155]">
                                      {formatAddonLabel(choice.priceAddon)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}

                          {option.type === "multiple_choice" ? (
                            <div className="mt-3 space-y-2">
                              {option.choices.map((choice) => {
                                const selectedKeys = Array.isArray(value) ? value : [];
                                const checked = selectedKeys.includes(choice._key);

                                return (
                                  <button
                                    key={choice._key}
                                    type="button"
                                    onClick={() => toggleMultipleChoice(option._key, choice._key)}
                                    className="flex w-full items-center gap-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                                    style={checked ? selectedRowStyle : undefined}
                                  >
                                    <span
                                      className="inline-flex h-4 w-4 items-center justify-center rounded border border-[#94a3b8]"
                                      style={checked ? { borderColor: primaryColor, backgroundColor: primaryColor } : undefined}
                                    >
                                      {checked ? <Check className="h-3 w-3 text-white" /> : null}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-[#0f172a]">{choice.label}</p>
                                      {choice.description ? (
                                        <p className="truncate text-xs text-[#64748b]">{choice.description}</p>
                                      ) : null}
                                    </div>
                                    <span className="text-xs font-medium text-[#334155]">
                                      {formatAddonLabel(choice.priceAddon)}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="border-b border-[#e2e8f0] px-5 py-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-primary)]">
                      Quantità
                    </p>
                    <p className="mt-1 text-xs text-[#64748b]">
                      {soldOut
                        ? "Disponibilità: esaurito"
                        : inventoryManagement && maxQuantity
                          ? `Disponibilità: ${maxQuantity}`
                          : null}
                    </p>
                  </div>

                  <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <button
                      type="button"
                      onClick={() => canDecrement && setQuantity((current) => Math.max(1, current - 1))}
                      disabled={!canDecrement}
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="min-w-5 text-center text-sm font-semibold text-[#020617]">{quantity}</span>
                    <button
                      type="button"
                      onClick={() => canIncrement && setQuantity((current) => current + 1)}
                      disabled={!canIncrement}
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </section>

              {extraInfoBlocks.length ? (
                <section className="border-b border-[#e2e8f0] px-5 py-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-primary)]">
                    Informazioni prodotto
                  </p>

                  <div className="mt-4 space-y-4">
                    {extraInfoBlocks.map((block) => {
                      if (block.type === "text") {
                        return (
                          <div key={block._key} className="rounded-xl border border-[#e2e8f0] bg-white p-3">
                            <p className="text-sm leading-5 text-[#334155]">{block.content}</p>
                          </div>
                        );
                      }

                      if (block.type === "faq") {
                        return (
                          <div key={block._key} className="rounded-xl border border-[#e2e8f0] bg-white p-3">
                            {block.title ? (
                              <p className="mb-3 text-sm font-semibold text-[#0f172a]">{block.title}</p>
                            ) : null}
                            <div className="space-y-3">
                              {block.items.map((item) => (
                                <div key={item._key} className="rounded-lg bg-[#f8fafc] p-3">
                                  <p className="text-sm font-semibold text-[#0f172a]">{item.question}</p>
                                  <p className="mt-1 text-sm text-[#475569]">{item.answer}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={block._key} className="rounded-xl border border-[#e2e8f0] bg-white p-3">
                          {block.title ? <p className="mb-3 text-sm font-semibold text-[#0f172a]">{block.title}</p> : null}
                          <div className="overflow-x-auto rounded-lg border border-[#e2e8f0]">
                            <table className="min-w-full border-collapse text-left text-sm">
                              <thead>
                                <tr className="bg-[#f8fafc]">
                                  {block.headers.map((header, index) => (
                                    <th key={`${block._key}-h-${index}`} className="border-b border-[#e2e8f0] px-3 py-2 font-semibold text-[#334155]">
                                      {header}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {block.rows.map((row, rowIndex) => (
                                  <tr key={`${block._key}-r-${rowIndex}`}>
                                    {block.headers.map((_, cellIndex) => (
                                      <td key={`${block._key}-c-${rowIndex}-${cellIndex}`} className="border-b border-[#e2e8f0] px-3 py-2 text-[#475569]">
                                        {row[cellIndex] || "—"}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}

              <section className="px-5 py-6">
                <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[var(--tenant-primary)]">Note</p>
                <p className="mt-1 text-xs text-[#64748b]">Scrivi qui ulteriori indicazioni o richieste:</p>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  placeholder="Scrivi note..."
                  className="mt-5 w-full rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm text-[#020617] outline-none focus:border-[var(--tenant-primary)]"
                />
              </section>
            </main>

            <footer className="fixed bottom-0 left-1/2 z-20 w-full max-w-[800px] -translate-x-1/2 rounded-t-3xl bg-white px-5 pb-10 pt-4 shadow-[0_-3px_12px_rgba(0,0,0,0.08)]">
              {optionsAddonTotal !== 0 ? (
                <div className="mb-2 flex items-center justify-between text-xs text-[#64748b]">
                  <span>Extra opzioni</span>
                  <span className="font-semibold text-[#334155]">{formatAddonLabel(optionsAddonTotal)}</span>
                </div>
              ) : null}

              {missingRequiredCount > 0 ? (
                <p className="mb-2 text-xs text-[#b45309]">
                  Completa {missingRequiredCount} opzion{missingRequiredCount > 1 ? "i" : "e"} obbligatori{missingRequiredCount > 1 ? "e" : "a"}.
                </p>
              ) : null}

              <button
                type="button"
                disabled={ctaDisabled}
                className="flex h-11 w-full items-center justify-between rounded-lg bg-[var(--tenant-accent)] px-6 text-sm font-semibold text-white disabled:opacity-50"
              >
                <span>AGGIUNGI ALL'ORDINE</span>
                {totalPrice > 0 ? <span className="text-base font-bold">{formatCurrency(totalPrice)}</span> : null}
              </button>
            </footer>
          </>
        ) : null}

        {!loading && !error && !product ? (
          <div className="px-5 py-6 text-sm text-[#64748b]">
            Prodotto non disponibile.{" "}
            <Link to={`/${tenantId}`} className="underline">
              Torna al catalogo
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
